// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - SIMULATEUR IoT INTÉGRÉ
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy / Joshua - Janvier 2026
// Fichier : frontend/src/modules/admin/components/SimulateurIoT.jsx
//
// Simule les capteurs IoT et écrit dans la BDD comme si c'étaient de vrais
// ESP32, sans envoyer de commandes physiques.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Cpu, Thermometer, Droplets, Sun, Moon, Wind, AlertTriangle,
    CheckCircle, Play, Pause, RefreshCw, Save, Zap, Cloud, CloudRain,
    Sprout, Calendar, Activity, Settings, Radio, Wifi, WifiOff
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ═══════════════════════════════════════════════════════════════════════════
// PHASES LUNAIRES
// ═══════════════════════════════════════════════════════════════════════════
const PHASES_LUNE = [
    { nom: 'Nouvelle Lune', icone: '🌑', code: 'NEW', conseil: '⏸️ Repos - Éviter semis', jardin: 'repos' },
    { nom: 'Premier Croissant', icone: '🌒', code: 'WAXING_CRESCENT', conseil: '🌿 Semis feuilles', jardin: 'feuille' },
    { nom: 'Premier Quartier', icone: '🌓', code: 'FIRST_QUARTER', conseil: '🌿 Semis feuilles/fruits', jardin: 'feuille' },
    { nom: 'Gibbeuse Croissante', icone: '🌔', code: 'WAXING_GIBBOUS', conseil: '🍅 Semis fruits', jardin: 'fruit' },
    { nom: 'Pleine Lune', icone: '🌕', code: 'FULL', conseil: '✨ Récolte optimale', jardin: 'recolte' },
    { nom: 'Gibbeuse Décroissante', icone: '🌖', code: 'WANING_GIBBOUS', conseil: '🥕 Semis racines', jardin: 'racine' },
    { nom: 'Dernier Quartier', icone: '🌗', code: 'LAST_QUARTER', conseil: '🥕 Semis racines', jardin: 'racine' },
    { nom: 'Dernier Croissant', icone: '🌘', code: 'WANING_CRESCENT', conseil: '🧹 Nettoyage, taille', jardin: 'repos' }
];

function getPhaseLune(date) {
    const d = new Date(date);
    const refDate = new Date(2000, 0, 6, 18, 14);
    const diff = d - refDate;
    const jours = diff / (1000 * 60 * 60 * 24);
    const cycleLunaire = 29.53059;
    const phase = ((jours % cycleLunaire) + cycleLunaire) % cycleLunaire;
    const index = Math.floor((phase / cycleLunaire) * 8) % 8;
    return {
        ...PHASES_LUNE[index],
        age: Math.floor(phase),
        pourcentage: Math.round((phase / cycleLunaire) * 100),
        illumination: Math.round(Math.abs(Math.cos((phase / cycleLunaire) * 2 * Math.PI)) * 100)
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// FLAGS DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════
const FLAGS_DEFINITIONS = {
    ARR: { nom: 'Irrigation', icone: '💧', couleur: 'blue' },
    GEL: { nom: 'Risque Gel', icone: '❄️', couleur: 'cyan' },
    SEC: { nom: 'Sécheresse', icone: '🏜️', couleur: 'orange' },
    EAU: { nom: 'Excès Eau', icone: '🌊', couleur: 'blue' },
    TMP: { nom: 'Température', icone: '🌡️', couleur: 'red' },
    REC: { nom: 'Récolte', icone: '🌾', couleur: 'green' },
    LUM: { nom: 'Luminosité', icone: '☀️', couleur: 'yellow' }
};

// ═══════════════════════════════════════════════════════════════════════════
// ACTIONNEURS DISPONIBLES
// ═══════════════════════════════════════════════════════════════════════════
const ACTIONNEURS = {
    vanne: { nom: 'Vanne Irrigation', icone: '🚿', flag: 'ARR' },
    voile: { nom: 'Voile P17', icone: '🛡️', flag: 'GEL' },
    ventilation: { nom: 'Ventilation', icone: '🌀', flag: 'TMP' },
    chauffage: { nom: 'Chauffage', icone: '🔥', flag: 'GEL' },
    brumisation: { nom: 'Brumisation', icone: '💨', flag: 'TMP' },
    eclairage: { nom: 'Éclairage LED', icone: '💡', flag: 'LUM' },
    brasseur: { nom: 'Brasseur Air', icone: '🔄', flag: 'EAU' }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
const SimulateurIoT = () => {
    // ─── États ────────────────────────────────────────────────────────────
    const [parcelles, setParcelles] = useState([]);
    const [selectedParcelle, setSelectedParcelle] = useState(null);
    const [ficheCulturale, setFicheCulturale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoMode, setAutoMode] = useState(true);
    const [isRunning, setIsRunning] = useState(false);
    const [dateSimulee, setDateSimulee] = useState(new Date().toISOString().split('T')[0]);
    
    // Capteurs
    const [capteurs, setCapteurs] = useState({
        temp_air: 18.0,
        temp_sol: 14.0,
        hum_air: 65,
        hum_sol: 40,
        luminosite: 35000,
        heures_soleil: 8
    });
    
    // Actionneurs
    const [actionneurs, setActionneurs] = useState({
        vanne: false,
        voile: false,
        ventilation: false,
        chauffage: false,
        brumisation: false,
        eclairage: false,
        brasseur: false
    });
    
    // Flags actifs
    const [flagsActifs, setFlagsActifs] = useState([]);
    
    // Historique/Journal
    const [journal, setJournal] = useState([]);
    
    // Météo réelle
    const [meteoReelle, setMeteoReelle] = useState(null);
    
    // Interval ref
    const intervalRef = useRef(null);

    // ─── Chargement initial ───────────────────────────────────────────────
    useEffect(() => {
        loadParcelles();
    }, []);

    const loadParcelles = async () => {
        try {
            const res = await fetch(`${API_BASE}/simulateur-iot/parcelles-actives`);
            const data = await res.json();
            if (data.success) {
                setParcelles(data.data);
                if (data.data.length > 0) {
                    selectParcelle(data.data[0]);
                }
            }
        } catch (err) {
            console.error('Erreur chargement parcelles:', err);
            addToJournal('error', 'Erreur chargement parcelles');
        } finally {
            setLoading(false);
        }
    };

    const selectParcelle = async (parcelle) => {
        setSelectedParcelle(parcelle);
        
        // Charger la fiche culturale
        if (parcelle.variete_id) {
            try {
                const res = await fetch(`${API_BASE}/simulateur-iot/fiche-culturale/${parcelle.variete_id}`);
                const data = await res.json();
                if (data.success) {
                    setFicheCulturale(data.data);
                }
            } catch (err) {
                console.error('Erreur fiche culturale:', err);
            }
        }
        
        // Charger les flags actifs
        if (parcelle.culture_id) {
            try {
                const res = await fetch(`${API_BASE}/simulateur-iot/flags-actifs/${parcelle.culture_id}`);
                const data = await res.json();
                if (data.success) {
                    setFlagsActifs(data.data);
                }
            } catch (err) {
                console.error('Erreur flags:', err);
            }
        }
        
        addToJournal('info', `Parcelle sélectionnée: ${parcelle.parcelle_complete}`);
    };

    // ─── Journal ──────────────────────────────────────────────────────────
    const addToJournal = (type, message) => {
        const entry = {
            timestamp: new Date().toLocaleTimeString('fr-FR'),
            type,
            message
        };
        setJournal(prev => [entry, ...prev].slice(0, 50));
    };

    // ─── Calcul du stade actuel ───────────────────────────────────────────
    const getStadeActuel = useCallback(() => {
        if (!selectedParcelle || !ficheCulturale?.stades) return null;
        
        const joursDepuisPlantation = selectedParcelle.jours_depuis_plantation || 0;
        const stades = ficheCulturale.stades;
        
        let joursAccumules = 0;
        for (const stade of stades) {
            joursAccumules += stade.duree_jours_type || 30;
            if (joursDepuisPlantation <= joursAccumules) {
                return {
                    ...stade,
                    progression: Math.round(((joursDepuisPlantation - (joursAccumules - (stade.duree_jours_type || 30))) / (stade.duree_jours_type || 30)) * 100)
                };
            }
        }
        return stades[stades.length - 1]; // Dernier stade
    }, [selectedParcelle, ficheCulturale]);

    // ─── Évaluation des conditions ────────────────────────────────────────
    const evaluerConditions = useCallback(() => {
        const stade = getStadeActuel();
        if (!stade) return [];
        
        const flags = [];
        
        // Humidité sol
        if (capteurs.hum_sol < (stade.seuil_humidite_min || 25)) {
            flags.push({ code: 'SEC', raison: `Humidité sol ${capteurs.hum_sol}% < seuil ${stade.seuil_humidite_min}%`, valeur: capteurs.hum_sol });
        }
        if (capteurs.hum_sol > (stade.seuil_humidite_max || 55)) {
            flags.push({ code: 'EAU', raison: `Humidité sol ${capteurs.hum_sol}% > seuil ${stade.seuil_humidite_max}%`, valeur: capteurs.hum_sol });
        }
        
        // Température
        if (capteurs.temp_sol < (stade.temp_sol_min || 8)) {
            flags.push({ code: 'GEL', raison: `Température sol ${capteurs.temp_sol}°C < seuil ${stade.temp_sol_min}°C`, valeur: capteurs.temp_sol });
        }
        if (capteurs.temp_air > 35) {
            flags.push({ code: 'TMP', raison: `Température air ${capteurs.temp_air}°C > 35°C`, valeur: capteurs.temp_air });
        }
        
        // Luminosité
        const besoinLux = stade.besoin_luminosite_lux || 30000;
        if (capteurs.luminosite < besoinLux * 0.6) {
            flags.push({ code: 'LUM', raison: `Luminosité ${capteurs.luminosite} lux < 60% du besoin`, valeur: capteurs.luminosite });
        }
        
        // Besoin irrigation (Kc * ET0)
        const etc = (stade.kc || 1.0) * 4.5; // ET0 approximatif
        if (capteurs.hum_sol < (stade.seuil_humidite_opt || 40)) {
            flags.push({ code: 'ARR', raison: `Irrigation recommandée - ETc: ${etc.toFixed(1)}mm`, valeur: etc });
        }
        
        return flags;
    }, [capteurs, getStadeActuel]);

    // ─── Actions automatiques ─────────────────────────────────────────────
    const executerActionsAuto = useCallback(async () => {
        if (!autoMode || !selectedParcelle) return;
        
        const flagsCalcules = evaluerConditions();
        const newActionneurs = { ...actionneurs };
        
        // Reset
        Object.keys(newActionneurs).forEach(k => newActionneurs[k] = false);
        
        // Activer selon les flags
        for (const flag of flagsCalcules) {
            switch (flag.code) {
                case 'ARR':
                case 'SEC':
                    newActionneurs.vanne = true;
                    break;
                case 'GEL':
                    newActionneurs.voile = true;
                    newActionneurs.chauffage = true;
                    break;
                case 'TMP':
                    newActionneurs.ventilation = true;
                    newActionneurs.brumisation = true;
                    break;
                case 'EAU':
                    newActionneurs.ventilation = true;
                    newActionneurs.brasseur = true;
                    break;
                case 'LUM':
                    newActionneurs.eclairage = true;
                    break;
            }
        }
        
        setActionneurs(newActionneurs);
        
        // Générer les flags en BDD
        for (const flag of flagsCalcules) {
            try {
                await fetch(`${API_BASE}/simulateur-iot/generer-flag`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        culture_id: selectedParcelle.culture_id,
                        flag_code: flag.code,
                        raison: flag.raison,
                        valeur_declencheur: flag.valeur
                    })
                });
            } catch (err) {
                console.error('Erreur génération flag:', err);
            }
        }
        
        setFlagsActifs(flagsCalcules.map(f => ({
            code: f.code,
            raison: f.raison,
            ...FLAGS_DEFINITIONS[f.code]
        })));
        
    }, [autoMode, selectedParcelle, evaluerConditions, actionneurs]);

    // ─── Enregistrement des mesures ───────────────────────────────────────
    const enregistrerMesures = useCallback(async () => {
        if (!selectedParcelle) return;
        
        try {
            await fetch(`${API_BASE}/simulateur-iot/enregistrer-mesures`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    parcelle_id: selectedParcelle.parcelle_id,
                    culture_id: selectedParcelle.culture_id,
                    module_id: selectedParcelle.module_id,
                    capteurs
                })
            });
            
            addToJournal('success', 'Mesures enregistrées en BDD');
        } catch (err) {
            console.error('Erreur enregistrement:', err);
            addToJournal('error', 'Erreur enregistrement mesures');
        }
    }, [selectedParcelle, capteurs]);

    // ─── Boucle de simulation ─────────────────────────────────────────────
    useEffect(() => {
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                // Variation aléatoire des capteurs
                setCapteurs(prev => ({
                    temp_air: Math.max(0, Math.min(45, prev.temp_air + (Math.random() - 0.5) * 2)),
                    temp_sol: Math.max(0, Math.min(35, prev.temp_sol + (Math.random() - 0.5) * 1)),
                    hum_air: Math.max(20, Math.min(100, prev.hum_air + (Math.random() - 0.5) * 5)),
                    hum_sol: Math.max(10, Math.min(80, prev.hum_sol + (Math.random() - 0.5) * 3)),
                    luminosite: Math.max(0, Math.min(100000, prev.luminosite + (Math.random() - 0.5) * 5000)),
                    heures_soleil: prev.heures_soleil
                }));
            }, 2000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning]);

    // ─── Évaluation continue ──────────────────────────────────────────────
    useEffect(() => {
        if (autoMode && selectedParcelle) {
            executerActionsAuto();
        }
    }, [capteurs, autoMode, selectedParcelle, executerActionsAuto]);

    // ─── Météo réelle ─────────────────────────────────────────────────────
    const fetchMeteo = async () => {
        try {
            const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=49.28&longitude=0.87&current=temperature_2m,relative_humidity_2m,rain&daily=sunshine_duration&timezone=Europe/Paris');
            const data = await res.json();
            
            setMeteoReelle({
                temp: data.current.temperature_2m,
                humidite: data.current.relative_humidity_2m,
                pluie: data.current.rain,
                ensoleillement: Math.round((data.daily.sunshine_duration?.[0] || 0) / 3600)
            });
            
            addToJournal('success', 'Météo récupérée');
        } catch (err) {
            addToJournal('error', 'Erreur météo');
        }
    };

    const appliquerMeteo = () => {
        if (meteoReelle) {
            setCapteurs(prev => ({
                ...prev,
                temp_air: meteoReelle.temp,
                hum_air: meteoReelle.humidite,
                heures_soleil: meteoReelle.ensoleillement
            }));
            addToJournal('info', 'Météo appliquée aux capteurs');
        }
    };

    // ─── Phase lunaire ────────────────────────────────────────────────────
    const phaseLune = getPhaseLune(dateSimulee);

    // ─── Stade actuel ─────────────────────────────────────────────────────
    const stadeActuel = getStadeActuel();

    // ─── Calcul ETc ───────────────────────────────────────────────────────
    const etc = stadeActuel ? ((stadeActuel.kc || 1.0) * 4.5).toFixed(1) : '0.0';

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-2">Chargement du simulateur...</span>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-screen text-white p-4">
            <div className="max-w-7xl mx-auto">
                
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Cpu className="w-8 h-8 text-green-400" />
                        🎮 Simulateur IoT Intégré
                    </h1>
                    
                    <div className="flex items-center gap-4">
                        {/* Mode Simulation Badge */}
                        <div className="flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm">
                            <Radio className="w-4 h-4" />
                            MODE SIMULATION
                        </div>
                        
                        {/* Boutons contrôle */}
                        <button
                            onClick={() => setIsRunning(!isRunning)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                                isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                            }`}
                        >
                            {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            {isRunning ? 'Pause' : 'Démarrer'}
                        </button>
                        
                        <button
                            onClick={enregistrerMesures}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg"
                        >
                            <Save className="w-4 h-4" />
                            Sauver BDD
                        </button>
                    </div>
                </div>

                {/* Sélecteur Parcelle */}
                <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
                    <div className="flex items-center gap-4">
                        <label className="text-sm text-slate-400">Parcelle/Culture :</label>
                        <select
                            value={selectedParcelle?.parcelle_id || ''}
                            onChange={(e) => {
                                const p = parcelles.find(p => p.parcelle_id === parseInt(e.target.value));
                                if (p) selectParcelle(p);
                            }}
                            className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                        >
                            {parcelles.map(p => (
                                <option key={p.parcelle_id} value={p.parcelle_id}>
                                    {p.variete_icone} {p.parcelle_complete} - {p.variete_nom}
                                </option>
                            ))}
                        </select>
                        
                        <input
                            type="date"
                            value={dateSimulee}
                            onChange={(e) => setDateSimulee(e.target.value)}
                            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                        />
                        
                        <button
                            onClick={() => setAutoMode(!autoMode)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                                autoMode ? 'bg-green-500' : 'bg-slate-600'
                            }`}
                        >
                            <Zap className="w-4 h-4" />
                            {autoMode ? 'AUTO' : 'MANUEL'}
                        </button>
                    </div>
                </div>

                {/* Grid principal */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    
                    {/* COL 1 : Infos Culture + Stade */}
                    <div className="space-y-4">
                        {/* Culture */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                <Sprout className="w-5 h-5 text-green-400" />
                                Culture
                            </h2>
                            {selectedParcelle && (
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Variété</span>
                                        <span>{selectedParcelle.variete_icone} {selectedParcelle.variete_nom}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Planté le</span>
                                        <span>{new Date(selectedParcelle.date_plantation).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Jour</span>
                                        <span className="text-green-400 font-bold">J+{selectedParcelle.jours_depuis_plantation}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">Module IoT</span>
                                        <span className={selectedParcelle.module_id ? 'text-green-400' : 'text-yellow-400'}>
                                            {selectedParcelle.code_module || 'Non assigné'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Stade actuel */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3">🌱 Stade Actuel</h2>
                            {stadeActuel && (
                                <div className="space-y-2">
                                    <div className="text-xl font-bold text-green-400">{stadeActuel.nom}</div>
                                    <div className="text-sm text-slate-400">Code: {stadeActuel.code_stade}</div>
                                    <div className="bg-slate-700 rounded-full h-2 overflow-hidden">
                                        <div 
                                            className="bg-green-500 h-full transition-all"
                                            style={{ width: `${stadeActuel.progression || 0}%` }}
                                        />
                                    </div>
                                    <div className="text-xs text-slate-400 text-center">{stadeActuel.progression}%</div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                        <div className="bg-slate-700/50 rounded p-2 text-center">
                                            <div className="text-cyan-400 font-mono text-lg">{stadeActuel.kc}</div>
                                            <div className="text-slate-400">Kc</div>
                                        </div>
                                        <div className="bg-slate-700/50 rounded p-2 text-center">
                                            <div className="text-cyan-400 font-mono text-lg">{etc}mm</div>
                                            <div className="text-slate-400">ETc/j</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Phase Lunaire */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3">🌙 Lune</h2>
                            <div className="text-center">
                                <div className="text-5xl mb-2">{phaseLune.icone}</div>
                                <div className="font-medium">{phaseLune.nom}</div>
                                <div className="text-xs text-slate-400 mt-1">Jour {phaseLune.age}</div>
                                <div className="text-sm text-yellow-400 mt-2">{phaseLune.conseil}</div>
                            </div>
                        </div>
                    </div>

                    {/* COL 2 : Capteurs */}
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-cyan-400" />
                            Capteurs
                            {isRunning && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                        </h2>
                        
                        <div className="space-y-4">
                            {/* Température Air */}
                            <CapteurSlider
                                label="🌡️ Temp. Air"
                                value={capteurs.temp_air}
                                min={-10}
                                max={45}
                                step={0.5}
                                unite="°C"
                                onChange={(v) => setCapteurs(p => ({ ...p, temp_air: v }))}
                                getColor={(v) => v < 5 ? '#3b82f6' : v > 30 ? '#ef4444' : '#22c55e'}
                            />
                            
                            {/* Température Sol */}
                            <CapteurSlider
                                label="🌡️ Temp. Sol"
                                value={capteurs.temp_sol}
                                min={0}
                                max={35}
                                step={0.5}
                                unite="°C"
                                onChange={(v) => setCapteurs(p => ({ ...p, temp_sol: v }))}
                                getColor={(v) => v < (stadeActuel?.temp_sol_min || 8) ? '#3b82f6' : v > (stadeActuel?.temp_sol_max || 25) ? '#ef4444' : '#22c55e'}
                            />
                            
                            {/* Humidité Air */}
                            <CapteurSlider
                                label="💨 Hum. Air"
                                value={capteurs.hum_air}
                                min={20}
                                max={100}
                                step={1}
                                unite="%"
                                onChange={(v) => setCapteurs(p => ({ ...p, hum_air: v }))}
                                getColor={(v) => v < 40 ? '#f59e0b' : v > 80 ? '#3b82f6' : '#22c55e'}
                            />
                            
                            {/* Humidité Sol */}
                            <CapteurSlider
                                label="💧 Hum. Sol"
                                value={capteurs.hum_sol}
                                min={10}
                                max={80}
                                step={1}
                                unite="%"
                                onChange={(v) => setCapteurs(p => ({ ...p, hum_sol: v }))}
                                getColor={(v) => {
                                    const min = stadeActuel?.seuil_humidite_min || 25;
                                    const max = stadeActuel?.seuil_humidite_max || 55;
                                    return v < min ? '#ef4444' : v > max ? '#3b82f6' : '#22c55e';
                                }}
                                reference={stadeActuel ? { min: stadeActuel.seuil_humidite_min, opt: stadeActuel.seuil_humidite_opt, max: stadeActuel.seuil_humidite_max } : null}
                            />
                            
                            {/* Luminosité */}
                            <CapteurSlider
                                label="☀️ Luminosité"
                                value={capteurs.luminosite}
                                min={0}
                                max={100000}
                                step={1000}
                                unite=" lux"
                                onChange={(v) => setCapteurs(p => ({ ...p, luminosite: v }))}
                                getColor={(v) => v < 20000 ? '#ef4444' : v < 30000 ? '#f59e0b' : '#22c55e'}
                            />
                            
                            {/* Heures Soleil */}
                            <CapteurSlider
                                label="🌤️ Soleil"
                                value={capteurs.heures_soleil}
                                min={0}
                                max={16}
                                step={0.5}
                                unite="h"
                                onChange={(v) => setCapteurs(p => ({ ...p, heures_soleil: v }))}
                                getColor={(v) => v < 4 ? '#ef4444' : v < 6 ? '#f59e0b' : '#22c55e'}
                            />
                        </div>
                    </div>

                    {/* COL 3 : Flags + Actionneurs */}
                    <div className="space-y-4">
                        {/* Flags */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                                Flags Actifs
                            </h2>
                            {flagsActifs.length === 0 ? (
                                <div className="text-center py-4 text-green-400">
                                    <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                                    Tout est OK
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {flagsActifs.map((flag, idx) => (
                                        <div 
                                            key={idx}
                                            className="flex items-center gap-2 p-2 rounded-lg bg-slate-700/50 border border-slate-600"
                                        >
                                            <span className="text-xl">{flag.icone}</span>
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{flag.nom}</div>
                                                <div className="text-xs text-slate-400">{flag.raison}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Actionneurs */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-yellow-400" />
                                Actionneurs
                                {autoMode && <span className="text-xs text-green-400">(AUTO)</span>}
                            </h2>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(ACTIONNEURS).map(([code, act]) => {
                                    const isOn = actionneurs[code];
                                    return (
                                        <button
                                            key={code}
                                            onClick={() => {
                                                if (!autoMode) {
                                                    setActionneurs(p => ({ ...p, [code]: !p[code] }));
                                                }
                                            }}
                                            disabled={autoMode}
                                            className={`p-2 rounded-lg border-2 transition-all text-center ${
                                                isOn 
                                                    ? 'bg-green-500/20 border-green-500 text-green-400' 
                                                    : 'bg-slate-700/50 border-slate-600 text-slate-400'
                                            } ${autoMode ? 'cursor-not-allowed opacity-70' : 'hover:border-slate-500'}`}
                                        >
                                            <div className="text-xl">{act.icone}</div>
                                            <div className="text-xs">{act.nom}</div>
                                            <div className={`text-xs ${isOn ? 'text-green-400' : 'text-slate-500'}`}>
                                                {isOn ? '● ON' : '○ OFF'}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* COL 4 : Météo + Journal */}
                    <div className="space-y-4">
                        {/* Météo */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                <Cloud className="w-5 h-5 text-blue-400" />
                                Météo Normandie
                            </h2>
                            <div className="flex gap-2 mb-3">
                                <button 
                                    onClick={fetchMeteo}
                                    className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm"
                                >
                                    📡 Récupérer
                                </button>
                                <button 
                                    onClick={appliquerMeteo}
                                    disabled={!meteoReelle}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                                        meteoReelle ? 'bg-green-500 hover:bg-green-600' : 'bg-slate-700 text-slate-500'
                                    }`}
                                >
                                    ✅ Appliquer
                                </button>
                            </div>
                            {meteoReelle && (
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-slate-700/50 rounded p-2 text-center">
                                        <div className="text-slate-400">Temp</div>
                                        <div className="font-mono text-lg">{meteoReelle.temp}°C</div>
                                    </div>
                                    <div className="bg-slate-700/50 rounded p-2 text-center">
                                        <div className="text-slate-400">Humidité</div>
                                        <div className="font-mono text-lg">{meteoReelle.humidite}%</div>
                                    </div>
                                    <div className="bg-slate-700/50 rounded p-2 text-center">
                                        <div className="text-slate-400">Pluie</div>
                                        <div className="font-mono text-lg">{meteoReelle.pluie}mm</div>
                                    </div>
                                    <div className="bg-slate-700/50 rounded p-2 text-center">
                                        <div className="text-slate-400">Soleil</div>
                                        <div className="font-mono text-lg">{meteoReelle.ensoleillement}h</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Journal */}
                        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <h2 className="text-lg font-semibold mb-3">📜 Journal</h2>
                            <div className="space-y-1 max-h-64 overflow-y-auto text-xs">
                                {journal.length === 0 ? (
                                    <div className="text-slate-500 text-center py-4">Aucun événement</div>
                                ) : (
                                    journal.map((entry, idx) => (
                                        <div 
                                            key={idx}
                                            className={`p-2 rounded ${
                                                entry.type === 'error' ? 'bg-red-500/20 text-red-400' :
                                                entry.type === 'success' ? 'bg-green-500/20 text-green-400' :
                                                'bg-slate-700/50 text-slate-300'
                                            }`}
                                        >
                                            <span className="text-slate-500">{entry.timestamp}</span> {entry.message}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-6 text-center text-slate-500 text-xs">
                    Farm-Booking IoT • Simulateur Intégré • 73+ Papy / Joshua • 2026
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT SLIDER CAPTEUR
// ═══════════════════════════════════════════════════════════════════════════
const CapteurSlider = ({ label, value, min, max, step, unite, onChange, getColor, reference }) => {
    const color = getColor(value);
    const percentage = ((value - min) / (max - min)) * 100;
    
    return (
        <div>
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm">{label}</span>
                <span className="font-mono text-sm" style={{ color }}>
                    {typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value}{unite}
                </span>
            </div>
            {reference && (
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Min: {reference.min}</span>
                    <span className="text-green-400">Opt: {reference.opt}</span>
                    <span>Max: {reference.max}</span>
                </div>
            )}
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full h-2 rounded-full cursor-pointer appearance-none"
                style={{
                    background: `linear-gradient(to right, ${color} ${percentage}%, #475569 ${percentage}%)`
                }}
            />
        </div>
    );
};

export default SimulateurIoT;